import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Stack,
    Card,
    CardContent,
    Chip,
    Button,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Add as AddIcon,
    Inventory as InventoryIcon,
    Assignment as AssignmentIcon,
    Description as DescriptionIcon,
    LocalShipping as ShippingIcon,
    Today as TodayIcon,
    Warning as WarningIcon,
    Build as BuildIcon,
} from '@mui/icons-material';
import CustomBox from '../../components/CustomBox/CustomBox';
import {
    documentsApi,
    DashboardData,
    DashboardAwizacja,
} from '../../services/documentsApi';
import { useWarehouse } from '../../contexts/WarehouseContext';

const DashboardPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { selectedWarehouse } = useWarehouse();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboard = async () => {
            if (!selectedWarehouse?.id) {
                setData(null);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const result = await documentsApi.getDashboard(selectedWarehouse.id);
                setData(result);
            } catch {
                setData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, [selectedWarehouse?.id]);

    const getDeliveryColor = () => 'primary';
    const getDeliveryBorderColor = () => theme.palette.primary.main;
    const getDeliveryBgColor = () => `${theme.palette.primary.main}20`;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    const stats = data?.stats;
    const rawAwizacje = data?.awizacje || [];
    const awizacje = rawAwizacje.filter(
        (a: DashboardAwizacja) =>
            a.document_type === 'A' || /^A-/.test(a.document_number || '')
    );
    const starredReports = data?.starred_reports || [];

    return (
        <Box sx={{
            p: { xs: 1.5, sm: 2, md: 3 },
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
            overflowX: 'hidden',
        }}>
            {/* Quick Access - Document Creation */}
            <CustomBox sx={{ mb: { xs: 2, md: 3 } }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" sx={{ mb: 2 }}>
                    {t('dashboard.quickActions')}
                </Typography>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                        },
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/documents/create?type=A')}
                        sx={{
                            width: '100%',
                            minHeight: { xs: 56, sm: 64 },
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            textTransform: 'none',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            fontWeight: 600,
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Stack alignItems="flex-start">
                            <Typography variant="body1" fontWeight={600}>{t('dashboard.newAwizacja')}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('dashboard.documentA')}</Typography>
                        </Stack>
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ShippingIcon />}
                        onClick={() => navigate('/documents/create?type=WZ')}
                        sx={{
                            width: '100%',
                            minHeight: { xs: 56, sm: 64 },
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            textTransform: 'none',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            fontWeight: 600,
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Stack alignItems="flex-start">
                            <Typography variant="body1" fontWeight={600}>{t('dashboard.newWydanie')}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('dashboard.documentWZ')}</Typography>
                        </Stack>
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<BuildIcon />}
                        onClick={() => navigate('/documents/create?type=KOR')}
                        sx={{
                            width: '100%',
                            minHeight: { xs: 56, sm: 64 },
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 2, sm: 3 },
                            textTransform: 'none',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            fontWeight: 600,
                            justifyContent: 'flex-start',
                            gridColumn: { xs: '1', sm: '1 / -1', md: 'auto' },
                        }}
                    >
                        <Stack alignItems="flex-start">
                            <Typography variant="body1" fontWeight={600}>{t('dashboard.newKorekta')}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('dashboard.documentKOR')}</Typography>
                        </Stack>
                    </Button>
                </Box>
            </CustomBox>

            {/* Stats Cards */}
            {stats && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                            lg: 'repeat(5, 1fr)',
                        },
                        gap: 2,
                        mb: { xs: 2, md: 3 },
                    }}
                >
                    <CustomBox sx={{ minWidth: 0, textAlign: 'center', py: 2 }}>
                        <WarningIcon sx={{ color: theme.palette.error.main, fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                        <Typography variant="h4" fontWeight="bold" color="error">{stats.awizacje_zaległe}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('dashboard.overdueAwizacje')}</Typography>
                    </CustomBox>
                    <CustomBox sx={{ minWidth: 0, textAlign: 'center', py: 2 }}>
                        <TodayIcon sx={{ color: theme.palette.warning.main, fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                        <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.warning.main }}>{stats.awizacje_dzisiaj}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('dashboard.awizacjeToday')}</Typography>
                    </CustomBox>
                    <CustomBox sx={{ minWidth: 0, textAlign: 'center', py: 2 }}>
                        <InventoryIcon sx={{ color: theme.palette.info.main, fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                        <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.info.main }}>{stats.awizacje_oczekujace}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('dashboard.pendingAwizacje')}</Typography>
                    </CustomBox>
                    <CustomBox sx={{ minWidth: 0, textAlign: 'center', py: 2 }}>
                        <ShippingIcon sx={{ color: theme.palette.success.main, fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                        <Typography variant="h4" fontWeight="bold" sx={{ color: theme.palette.success.main }}>{stats.wz_w_realizacji}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('dashboard.wzInProgress')}</Typography>
                    </CustomBox>
                    <CustomBox sx={{ minWidth: 0, textAlign: 'center', py: 2 }}>
                        <DescriptionIcon sx={{ color: theme.palette.grey[400], fontSize: { xs: 28, md: 32 }, mb: 0.5 }} />
                        <Typography variant="h4" fontWeight="bold">{stats.dokumenty_dzisiaj}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('dashboard.documentsToday')}</Typography>
                    </CustomBox>
                </Box>
            )}

            {/* Main Content */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="stretch" sx={{ minWidth: 0 }}>
                {/* Awizacje */}
                <Box sx={{ flex: { xs: '1 1 auto', md: 7 }, minWidth: 0 }}>
                    <CustomBox>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 2,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <InventoryIcon color="primary" sx={{ flexShrink: 0 }} />
                                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" noWrap>
                                    {t('dashboard.pendingAwizacjeTitle')}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                onClick={() => navigate('/documents?type=A&status=oczekujaca')}
                                sx={{ color: 'text.secondary', textTransform: 'none', flexShrink: 0 }}
                            >
                                {t('dashboard.seeAll')}
                            </Button>
                        </Box>

                        {awizacje.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                {t('dashboard.noPendingAwizacje')}
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {awizacje.map((a: DashboardAwizacja) => (
                                    <Card
                                        key={a.id}
                                        sx={{
                                            cursor: 'pointer',
                                            borderLeft: `4px solid ${getDeliveryBorderColor()}`,
                                            backgroundColor: getDeliveryBgColor(),
                                            '&:hover': { boxShadow: 2 },
                                            transition: 'box-shadow 0.2s',
                                        }}
                                        elevation={0}
                                        onClick={() => navigate(`/documents/${a.id}`)}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                                <Typography variant="subtitle2" fontWeight="bold">
                                                    {a.document_number}
                                                </Typography>
                                                <Chip
                                                    label={t(`documents.statuses.${a.delivery_status}`) || a.delivery_status}
                                                    size="small"
                                                    color={getDeliveryColor() as any}
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {a.supplier_name || t('dashboard.noSupplier')}
                                            </Typography>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {a.delivery_date
                                                        ? `${t('dashboard.deliveryLabel')}: ${a.delivery_date}`
                                                        : `${t('dashboard.documentDateLabel')}: ${a.document_date}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {a.products_count} {a.products_count === 1 ? t('dashboard.product_one') : t('dashboard.product_other')}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </CustomBox>
                </Box>

                {/* Sidebar: Starred Reports */}
                <Box sx={{ flex: { xs: '1 1 auto', md: 5 }, minWidth: 0 }}>
                    <CustomBox>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 2,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <AssignmentIcon color="primary" sx={{ flexShrink: 0 }} />
                                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" noWrap>
                                    {t('dashboard.favoriteReports')}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                onClick={() => navigate('/reports')}
                                sx={{ color: 'text.secondary', textTransform: 'none', flexShrink: 0 }}
                            >
                                {t('dashboard.all')}
                            </Button>
                        </Box>

                        {starredReports.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                {t('dashboard.noFavoriteReports')} {t('dashboard.noFavoriteReportsHint')}
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {starredReports.map((r) => (
                                    <Card
                                        key={r.id}
                                        sx={{
                                            cursor: 'pointer',
                                            backgroundColor: theme.palette.background.paper,
                                            '&:hover': { boxShadow: 1 },
                                            transition: 'box-shadow 0.2s',
                                        }}
                                        elevation={0}
                                        onClick={() => navigate('/reports')}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Box display="flex" alignItems="center" gap={2}>
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {r.name}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                                        <Chip label={r.report_type_display} size="small" variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.7rem' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {t('dashboard.fieldsCount', { count: r.fields_count })}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                                <Box sx={{ color: 'primary.main', fontSize: '1.2rem' }}>★</Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </CustomBox>
                </Box>
            </Stack>
        </Box>
    );
};

export default DashboardPage;
